import { Pipeline, PipelineEnvironment, type PipelineRepository, PipelineStep } from '@telespot/domain';
import { deadlined, logged, retryable } from '@shared/decorators';
import { ParseBaseRepository } from '../../parse-base.repository';
import { ParsePipelineMapper, PipelineTopology } from './parse-pipeline.mapper';
import { ParsePipelineStepMapper, PipelineStepTopology } from './parse-pipeline-step.mapper';
import { ProtocolPipelineTopology } from './parse-protocol-pipeline.mapper';
import { MethodTypeTopology } from './parse-method-type.mapper';
import { ObjectTopology, SoftDeletableTopology } from '../../parse.topology';
import { AlgorithmTopology } from '../ai/parse-algorithm.mapper';

export class ParsePipelineRepository extends ParseBaseRepository implements PipelineRepository {
  private readonly pipelineMapper = new ParsePipelineMapper(this.parse);
  private readonly pipelineStepMapper = new ParsePipelineStepMapper(this.parse);

  private static readonly FIND_STEPS_PAGINATION_LIMIT = 50;

  /**
   * Reads a step query's complete result set. An unpaginated find silently
   * stops at Parse's 100-row cap, so a finder promising *every* matching step
   * pages through instead: ascending object id, `FIND_STEPS_PAGINATION_LIMIT`
   * rows at a time, keyset-cursored on the last id seen, until a page comes
   * back empty.
   *
   * Not `getTokenPaginationGenerator`, whose keyset mechanics are otherwise
   * the same: it terminates on a short page, which stops early when a
   * `beforeFind` hook trims one, and it reads with `this.options` instead of
   * the options it is handed. The finders need both differences — a page is
   * only trusted empty here, and `findStepsForMethodType` reads as master
   * while the rest use the repository's own options.
   *
   * The query is consumed rather than borrowed: its ordering and limit are
   * overwritten, and it is left holding the loop's final cursor.
   */
  private async findAcrossPages(
    query: Parse.Query,
    options: Parse.FullOptions
  ): Promise<Parse.Object<Parse.Attributes>[]> {
    const rows: Parse.Object<Parse.Attributes>[] = [];
    let lastId: string | undefined;

    query.ascending(ObjectTopology.ID);
    query.limit(ParsePipelineRepository.FIND_STEPS_PAGINATION_LIMIT);

    for (; ;) {
      if (lastId) query.greaterThan(ObjectTopology.ID, lastId);

      const page = await query.find(options);

      if (page.length === 0) return rows;

      rows.push(...page);
      lastId = page.at(-1)?.id;
    }
  }

  @retryable()
  @deadlined()
  @logged()
  public async getById(id: string): Promise<Pipeline> {
    const parsePipeline = await new this.parse.Query(PipelineTopology.TABLE).get(id, this.options);

    return this.pipelineMapper.toDomain(parsePipeline);
  }

  @retryable()
  @deadlined()
  @logged()
  public async getStepById(id: string): Promise<PipelineStep> {
    const parsePipelineStep = await new this.parse.Query(PipelineStepTopology.TABLE).get(id, this.options);

    return this.pipelineStepMapper.toDomain(parsePipelineStep);
  }

  @retryable()
  @deadlined()
  @logged()
  public async findStepsForPipeline(pipelineId: string): Promise<PipelineStep[]> {
    const ParsePipeline = this.subclasses.getSubclass(PipelineTopology.TABLE);

    const query = new this.parse.Query(PipelineStepTopology.TABLE).equalTo(
      PipelineStepTopology.PIPELINE,
      ParsePipeline.createWithoutData(pipelineId)
    );

    const results = await this.findAcrossPages(query, this.options);

    return results.map((p) => this.pipelineStepMapper.toDomain(p));
  }

  @retryable()
  @deadlined()
  @logged()
  public async findStepsForMethodType(methodTypeId: string): Promise<PipelineStep[]> {
    const methodType = this.subclasses.getSubclass(MethodTypeTopology.TABLE).createWithoutData(methodTypeId);

    const cloudPipelinesQuery = new this.parse.Query(PipelineTopology.TABLE).equalTo(
      PipelineTopology.ENV,
      PipelineEnvironment.CLOUD
    );

    const protocolPipelinesQuery = new this.parse.Query(ProtocolPipelineTopology.TABLE)
      .equalTo(ProtocolPipelineTopology.METHOD_TYPE, methodType)
      .matchesQuery(ProtocolPipelineTopology.PIPELINE, cloudPipelinesQuery);

    const includedFields = [PipelineStepTopology.EXECUTOR, PipelineStepTopology.PIPELINE];

    const query = new this.parse.Query(PipelineStepTopology.TABLE)
      .matchesKeyInQuery(PipelineStepTopology.PIPELINE, ProtocolPipelineTopology.PIPELINE, protocolPipelinesQuery)
      .include(includedFields);

    const results = await this.findAcrossPages(query, { useMasterKey: true });

    return results.map((p) => this.pipelineStepMapper.toDomain(p));
  }

  @retryable()
  @deadlined()
  @logged()
  public async findStepsForAlgorithm(algorithmId: string): Promise<PipelineStep[]> {
    const ParseAlgorithm = this.subclasses.getSubclass(AlgorithmTopology.TABLE);
    const algorithm = ParseAlgorithm.createWithoutData(algorithmId);

    const query = new this.parse.Query(PipelineStepTopology.TABLE).equalTo(PipelineStepTopology.EXECUTOR, algorithm);

    const results = await this.findAcrossPages(query, this.options);

    return results.map((p) => this.pipelineStepMapper.toDomain(p));
  }

  /**
   * Label usage lives inside the `params` blob, not in a column: a label is used
   * when it keys `params.options` or appears in any `params.categorization[].options`
   * array (Mongo matches array members through dot notation).
   */
  @retryable()
  @deadlined()
  @logged()
  public async findStepsForLabel(labelUuid: string): Promise<PipelineStep[]> {
    const usedAsOption = new this.parse.Query(PipelineStepTopology.TABLE).exists(
      `${PipelineStepTopology.PARAMS}.options.${labelUuid}`
    );

    const usedInCategorization = new this.parse.Query(PipelineStepTopology.TABLE).equalTo(
      `${PipelineStepTopology.PARAMS}.categorization.options`,
      labelUuid
    );

    const query = this.parse.Query.or(usedAsOption, usedInCategorization);

    const results = await this.findAcrossPages(query, this.options);

    return results.map((p) => this.pipelineStepMapper.toDomain(p));
  }

  /**
   * Cursor pagination sorts by `createdAt` descending so the newest items
   * appear first, with `lastItemCreatedAt` as the keyset cursor.
   */
  @retryable()
  @deadlined()
  @logged()
  public async getAll(
    _lang: string,
    searchTerm?: string,
    limit?: number,
    lastItemCreatedAt?: Date,
    env?: PipelineEnvironment
  ): Promise<Pipeline[]> {
    const query = this.buildPipelineQuery(searchTerm, env);
    query.descending(ObjectTopology.CREATED_AT);

    if (limit) {
      query.limit(limit);
    }

    if (lastItemCreatedAt) {
      query.lessThan(ObjectTopology.CREATED_AT, lastItemCreatedAt);
    }

    const parsePipelines = await query.find(this.options);
    return parsePipelines.map((pp) => this.pipelineMapper.toDomain(pp));
  }

  private static readonly COUNT_PIPELINES_PAGINATION_LIMIT = 25;

  @retryable()
  @deadlined()
  @logged()
  public async count(searchTerm?: string, env?: PipelineEnvironment): Promise<number> {
    let total = 0;
    let lastId: string | undefined;
    let continueQuerying = true;

    while (continueQuerying) {
      const query = this.buildPipelineQuery(searchTerm, env);
      query.ascending(ObjectTopology.ID);
      query.select();
      query.limit(ParsePipelineRepository.COUNT_PIPELINES_PAGINATION_LIMIT);

      if (lastId) {
        query.greaterThan(ObjectTopology.ID, lastId);
      }

      const results = await query.find(this.options);
      total += results.length;
      continueQuerying = results.length === ParsePipelineRepository.COUNT_PIPELINES_PAGINATION_LIMIT;

      if (continueQuerying && results.length > 0) {
        lastId = results.at(-1).id;
      }
    }

    return total;
  }

  @retryable()
  @deadlined()
  @logged()
  public async countStepsForPipeline(pipelineId: string): Promise<number> {
    const parsePipeline = this.subclasses.getSubclass(PipelineTopology.TABLE).createWithoutData(pipelineId);

    return await new this.parse.Query(PipelineStepTopology.TABLE)
      .equalTo(PipelineStepTopology.PIPELINE, parsePipeline)
      .count(this.options);
  }

  private buildPipelineQuery(searchTerm?: string, env?: PipelineEnvironment) {
    const query = new this.parse.Query(PipelineTopology.TABLE);

    if (searchTerm) {
      query.matches(`${PipelineTopology.NAME}.searchTerm`, new RegExp(this.escapeRegExp(searchTerm), 'i'));
    }

    if (env) {
      query.equalTo(PipelineTopology.ENV, env);
    }

    return query;
  }

  public async saveStep(step: PipelineStep): Promise<string> {
    const parseStep = this.pipelineStepMapper.fromDomain(step);
    const savedStep = await parseStep.save(null, this.options);
    return savedStep.id;
  }

  public async savePipeline(pipeline: Pipeline): Promise<string> {
    const parsePipeline = this.pipelineMapper.fromDomain(pipeline);
    const savedPipeline = await parsePipeline.save(null, this.options);
    return savedPipeline.id;
  }

  public async delete(id: string): Promise<void> {
    const parseStep = this.subclasses.getSubclass(PipelineStepTopology.TABLE).createWithoutData(id);
    await parseStep.save(
      {
        [SoftDeletableTopology.DELETED_AT]: new Date(),
      },
      this.options
    );
  }
}
